import { getGlobalSettings, useGetCurrencySymbol } from 'app/appState';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { AppRoutes } from 'app/config/routerConfig/types';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { BoxForListing as Box, BoxForListing, useGetPaginatedBoxListingQuery } from 'entities/Box';
import { UnitType } from 'entities/Contract';
import { UserAccountType, useRole, UserPermissions } from 'entities/User';
import { useGetVatRateQuery, VatRateParams } from 'entities/VatRate';
import { useGetAllWarehousesQuery } from 'entities/Warehouse';
import { boxFormDrawerActions, DrawerMode } from 'features/BoxFormDrawer';
import { CancelReservationModal } from 'features/CancelReservationModal';
import { ExportEntity, getBoxColumnKeys, useOpenExportModal } from 'features/ExportModal';
import { useOpenBox } from 'features/OpenBox';
import { useTableFilterContext } from 'features/TableFilter';
import { TableRowFullInfoModal, useRowFullInfo } from 'features/TableRowFullInfoModal';
import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as BoxInfoIcon } from 'shared/assets/icons/BoxInfoIcon.svg';
import { ReactComponent as DeleteIcon } from 'shared/assets/icons/DeleteIcon.svg';
import { ReactComponent as BoxEditIcon } from 'shared/assets/icons/EditIcon.svg';
import { ReactComponent as WalletIcon } from 'shared/assets/icons/WalletIcon.svg';
import { ReactComponent as DiscountIcon } from 'shared/assets/icons/DiscountIcon.svg';
import { ReactComponent as ExportIcon } from 'shared/assets/icons/ExportIcon.svg';
import { ReactComponent as FiltersIcon } from 'shared/assets/icons/FiltersIcon.svg';
import { ReactComponent as KeyIcon } from 'shared/assets/icons/KeyIcon.svg';
import { ReactComponent as PlusIcon } from 'shared/assets/icons/PlusIcon.svg';
import { ServerTable } from 'shared/ui/ServerTable';
import { ServerTableActions, ServerTableRowActions } from 'shared/ui/ServerTable/types';
import { useQueryString } from 'shared/utils/helpers/convertQueryString';
import { useCheckPermission } from 'shared/utils/hooks/useCheckPermission';
import { useScreenBreakpoints } from 'shared/utils/hooks/useScreenBreakpoints';
import { ContractDetailsDrawer, contractDetailsDrawerActions } from 'widgets/ContractDetailsDrawer';
import { reservationDetailDrawerActions, ReservationDetailsDrawer } from 'widgets/ReservationDetailsDrawer';
import { TakeUpReservationModal } from 'widgets/TakeUpReservationModal';
import { ModalMode } from '../model/types';
import { boxFullInfoMapper } from '../utils/helpers/boxFullInfoMapper';
import { transformBoxesListFilters } from '../utils/helpers/transformBoxesListFilters';
import { BoxesListFilter } from './BoxesListFilter';
import { BoxInfoCard } from './BoxInfoCard';
import { renderBoxListColumns } from './BoxListColumns';
import { UnitPriceHistoryModal } from 'features/UnitPriceHistoryModal';
import { changePricesModalActions } from 'widgets/ChangePricesModal';

const boxVatRateParams = {
  accountType: UserAccountType.PERSONAL,
  unitType: UnitType.BOX,
};

export const BoxesTable: FC = memo(() => {
  const { t } = useAppTranslation(['contracts', 'boxes', 'common']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { data: warehouses } = useGetAllWarehousesQuery();

  const globalSettings = useAppSelector(getGlobalSettings);
  const { isManagement } = useRole();
  const isForbiddenCreateBox = useCheckPermission(UserPermissions.CREATE_UNITS);
  const isForbiddenEditUnits = useCheckPermission(UserPermissions.EDIT_UNITS);
  const { setFiltersOpened, appliedFilters, tags, clearAllFilters } = useTableFilterContext();

  const [modalMode, setModalMode] = useState<Nullable<ModalMode>>(null);
  const [selectedBox, setSelectedBox] = useState<Nullable<Box>>(null);
  const [isPriceHistoryModalOpened, setIsPriceHistoryModalOpened] = useState(false);

  const { openExportModal, isExportEnabled } = useOpenExportModal();
  const { openFullInfoModal } = useRowFullInfo();
  const { isDesktop } = useScreenBreakpoints();
  const { executeBoxOpening } = useOpenBox();
  const { data: boxVatRate } = useGetVatRateQuery(boxVatRateParams as VatRateParams);
  const { getEncodedString } = useQueryString();

  const goToUserContracts = useCallback(
    (userId: string | undefined): void => {
      if (userId) {
        navigate(`${AppRoutes.USERS}/${userId}/contracts`);
      }
    },
    [navigate],
  );

  useEffect(() => {
    // TO DO INVESTIGATE HOW TO CLEAR ALL FILTERS WITHOUT USE EFFECT !!!
    return () => {
      clearAllFilters();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openPriceHistoryModal = useCallback((box: Box) => {
    setSelectedBox(box);
    setIsPriceHistoryModalOpened(true);
  }, []);

  const closePriceHistoryModal = useCallback(() => {
    setSelectedBox(null);
    setIsPriceHistoryModalOpened(false);
  }, []);

  const currencySymbol = useGetCurrencySymbol();

  const closeModal = useCallback((): void => {
    setModalMode(null);
    setSelectedBox(null);
  }, []);

  const openModal = useCallback((mode: ModalMode): void => {
    setModalMode(mode);
  }, []);

  const openBoxInfoModal = useCallback(
    (box: Box, mode = ModalMode.BOX_INFO): void => {
      setSelectedBox(box);
      openModal(mode);
    },
    [openModal],
  );

  const createBox = useCallback((): void => {
    dispatch(boxFormDrawerActions.setOpenBoxForm({ mode: DrawerMode.BOX_CREATE }));
  }, [dispatch]);

  const openChangePricesModal = useCallback((): void => {
    dispatch(changePricesModalActions.setOpened(true));
  }, [dispatch]);

  const editBox = useCallback(
    (box: Box): void => {
      dispatch(boxFormDrawerActions.setOpenBoxForm({ mode: DrawerMode.BOX_EDIT, box }));
    },
    [dispatch],
  );

  const openBox = useCallback(
    (box: Box): void => {
      executeBoxOpening({
        box,
        openByAdmin: true,
        isExternalBox: box.timeToOpenLockMS != null,
      });
    },
    [executeBoxOpening],
  );

  const openContractDetails = useCallback(
    (contractId: string | undefined): void => {
      if (contractId) {
        dispatch(contractDetailsDrawerActions.setOpenContractDetails(contractId));
      }
    },
    [dispatch],
  );

  const openReservationDetails = useCallback(
    (reservationId: string | undefined): void => {
      if (reservationId) {
        dispatch(reservationDetailDrawerActions.setOpenReservationDetails(reservationId));
      }
    },
    [dispatch],
  );

  const goToBoxHistory = useCallback(
    (boxName: string): void => {
      const encodedFilters = getEncodedString({
        search: { value: boxName, label: boxName },
      });

      const url = `${AppRoutes.HISTORY}/?search=${encodedFilters.search}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    [getEncodedString],
  );

  const rowActions: Array<ServerTableRowActions<Box>> = useMemo(
    () => [
      {
        name: 'editBox',
        icon: <BoxEditIcon />,
        theme: 'clear',
        description: t('Edit box'),
        onClick: editBox,
      },
      {
        name: 'openBox',
        theme: 'primary',
        label: t('Open'),
        icon: <KeyIcon className="stroke-white" />,
        iconPosition: 'prev',
        onClick: openBox,
      },
      {
        name: 'boxHistory',
        icon: <BoxInfoIcon />,
        theme: 'clear',
        description: t('Box history', { ns: 'boxes' }),
        label: t('Box history', { ns: 'boxes' }),
        onClick: (selectedBox) => {
          goToBoxHistory(selectedBox.name);
        },
      },
      {
        name: 'unitPriceHistory',
        icon: <WalletIcon className="!stroke-primary" />,
        theme: 'clear',
        description: t('Price history'),
        label: t('Price history'),
        onClick: (selectedBox) => {
          openPriceHistoryModal(selectedBox);
        },
      },
      {
        name: 'deleteBox',
        icon: <DeleteIcon />,
        theme: 'clear',
        description: t('Delete box'),
        label: t('Delete box'),
        onClick: (selectedBox) => {
          console.log(selectedBox);
        },
      },
    ],
    [editBox, goToBoxHistory, openBox, openPriceHistoryModal, t],
  );

  const tableActions: Array<ServerTableActions<Box>> = useMemo(() => {
    const exportAction: Array<ServerTableActions<Box>> = isExportEnabled
      ? [
          {
            name: t('Export', { ns: 'common' }),
            theme: 'secondary',
            icon: <ExportIcon />,
            iconPosition: 'prev',
            onClick: () => {
              openExportModal({
                filters: transformBoxesListFilters(appliedFilters),
                columns: getBoxColumnKeys(t),
                entity: ExportEntity.BOX,
              });
            },
          },
        ]
      : [];

    return [
      {
        name: t('Add Box'),
        icon: <PlusIcon />,
        iconPosition: 'prev',
        onClick: createBox,
        disabled: isForbiddenCreateBox,
      },
      {
        name: t('Change prices'),
        icon: <DiscountIcon />,
        iconPosition: 'prev',
        theme: 'secondary',
        onClick: openChangePricesModal,
        disabled: isForbiddenEditUnits,
      },
      ...exportAction,
      {
        name: t('Filters', { ns: 'boxes' }),
        icon: <FiltersIcon />,
        theme: 'secondary',
        iconPosition: 'prev',
        onClick: () => {
          setFiltersOpened(true);
        },
      },
    ];
  }, [
    t,
    createBox,
    isForbiddenCreateBox,
    isForbiddenEditUnits,
    openChangePricesModal,
    isExportEnabled,
    openExportModal,
    appliedFilters,
    setFiltersOpened,
  ]);

  const staffRowActions = rowActions.filter((row) => row.name === 'openBox');

  const openBoxFullInfoModal = useCallback(
    (data: AnyObject): void => {
      const { title, rows, status, note } = boxFullInfoMapper(data as BoxForListing, currencySymbol, t);

      openFullInfoModal(title, status, rows, note);
    },
    [currencySymbol, openFullInfoModal, t],
  );

  return (
    <div className="pt-4">
      <div className="font-semibold text-3xl mb-7">{t('Boxes List')}</div>
      <ServerTable
        rowActions={isManagement ? rowActions : staffRowActions}
        columns={renderBoxListColumns({
          isDesktop,
          t,
          openBoxInfoModal,
          currencySymbol,
          goToUserContracts,
          openContractDetails,
          openReservationDetails,
          warehouses: warehouses || [],
          showPriceWithVat: globalSettings?.showPriceWithVat || false,
          vatRate: boxVatRate,
        })}
        fetchData={useGetPaginatedBoxListingQuery}
        defaultFilters={transformBoxesListFilters(appliedFilters)}
        rowKey="boxId"
        search
        tags={tags}
        onAllFiltersClear={clearAllFilters}
        tableActions={tableActions}
        tableActionsPosition="prev"
        tableActionsLimit={5}
        searchPlaceholder={t('Enter box, customer info or lock number')}
        onRowClick={!isDesktop ? openBoxFullInfoModal : undefined}
        rowActionsLimit={2}
      />
      <BoxInfoCard isOpen={modalMode === ModalMode.BOX_INFO} onClose={closeModal} box={selectedBox} />
      <ContractDetailsDrawer />
      <ReservationDetailsDrawer />
      <CancelReservationModal />
      <TakeUpReservationModal />
      <BoxesListFilter />
      <UnitPriceHistoryModal isOpened={isPriceHistoryModalOpened} boxId={selectedBox?.boxId} onClose={closePriceHistoryModal} />
      {!isDesktop && <TableRowFullInfoModal />}
    </div>
  );
});
